var _a, _b;
import { EventEmitter } from '@spotify-internal/emitter';
import { RequestAbortedError, RequestTimeoutError } from '../error/request';
export const ABORT_REASON = 'Signal is aborted without reason';
export const TIMEOUT_REASON = 'Signal timed out';
function createAbortReason(name, message) {
    const error = new Error(message);
    error.name = name;
    return error;
}
export class AbortSignalMicrofill extends EventEmitter {
    constructor() {
        super();
        this._aborted = false;
        this._reason = undefined;
        this.onabort = null;
        this.on('abort', () => { var _a; return (_a = this.onabort) === null || _a === void 0 ? void 0 : _a.call(this); });
    }
    /**
     * Returns a new AbortSignalMicrofill that is already in an aborted state.
     *
     * @param reason - The reason why the operation was aborted.
     * @returns A new AbortSignalMicrofill instance that is already in an aborted state.
     */
    static abort(reason) {
        const signal = new AbortSignalMicrofill();
        signal._aborted = true;
        signal._reason = createAbortReason('AbortError', reason !== null && reason !== void 0 ? reason : ABORT_REASON);
        return signal;
    }
    /**
     * Non-standard method for aborting an AbortSignalLike.
     *
     * If the signal instance passed is an AbortSignalMicrofill, this function
     * will abort the signal and emit the event. Otherwise it'll be a noop.
     *
     * @param signal - Either an AbortSignalLike or an AbortSignalMicrofill instance.
     * @param reason - The reason why the operation was aborted.
     */
    static signalAbort(signal, reason) {
        if ('_aborted' in signal && !signal._aborted) {
            signal._aborted = true;
            if ('_reason' in signal) {
                signal._reason =
                    reason || createAbortReason('AbortError', ABORT_REASON);
            }
            signal.emit('abort', null);
        }
    }
    /**
     * Creates an AbortSignalLike instance that will be aborted after a specified timeout.
     *
     * @param ms - The number of milliseconds to wait before aborting.
     * @returns Return a compatible AbortSignal microfill that will be aborted
     *   after a specified timeout..
     */
    static timeout(ms) {
        if (!isFinite(ms)) {
            throw new TypeError(`Value of "delay" is not supported.`);
        }
        const signal = new AbortSignalMicrofill();
        setTimeout(() => {
            AbortSignalMicrofill.signalAbort(signal, createAbortReason('TimeoutError', TIMEOUT_REASON));
        }, ms);
        return signal;
    }
    get aborted() {
        return this._aborted;
    }
    set aborted(_value) { }
    get reason() {
        return this._reason;
    }
    addEventListener(type, listener) {
        this.addListener(type, listener);
    }
    removeEventListener(type, listener) {
        this.removeListener(type, listener);
    }
    dispatchEvent() { }
}
/**
 * A small AbortController polyfill that implements the AbortControllerLike interface.
 */
export class AbortControllerMicrofill {
    constructor() {
        /**
         * Returns the AbortSignal object associated with this object.
         */
        this.signal = new AbortSignalMicrofill();
    }
    /**
     * Invoking this method will set this object's AbortSignal's aborted flag and
     * signal to any observers that the associated activity is to be aborted.
     *
     * @param reason - The reason why the operation was aborted.
     */
    abort(reason) {
        const signal = this.signal;
        AbortSignalMicrofill.signalAbort(signal, reason);
    }
}
/**
 * Returns whether the platform has a native AbortController.
 *
 * @returns True if the platform has a native AbortController, false otherwise.
 */
export function hasNativeAbortController() {
    return (typeof window !== 'undefined' &&
        typeof window.AbortController === 'function');
}
/**
 * Returns a Transport compatible AbortControllerLike object.
 *
 * @returns For platforms that natively support AbortController, this will
 *   return a native AbortController object. Otherwise, this will return a
 *   compatible AbortController microfill.
 */
export const createAbortControllerLike = hasNativeAbortController()
    ? () => new AbortController()
    : () => new AbortControllerMicrofill();
/**
 * Takes in multiple signals and combines them into a single signal that will be
 * aborted if any of the provided signals are aborted.
 *
 * If the platform supports the native `AbortSignal.any()` method, this will be used.
 *
 * @param signals - An array of signals to observe.
 * @returns A new signal that would be aborted if any of the provided signals are aborted.
 */
export const anySignal = typeof window !== 'undefined' && typeof ((_a = window.AbortSignal) === null || _a === void 0 ? void 0 : _a.any) === 'function'
    ? window.AbortSignal.any
    : (signals) => {
        const controller = createAbortControllerLike();
        // Operate on a copy of the signals to ensure that changes to the array
        // argument do not change the function.
        const _signals = [...signals];
        const onAbort = () => {
            // Clean up all listeners.
            for (const signal of _signals) {
                signal.removeEventListener('abort', onAbort);
            }
            controller.abort();
        };
        for (const signal of [...signals]) {
            // Check if any of the signals is already aborted.
            if (signal.aborted) {
                controller.abort(signal.reason);
                return controller.signal;
            }
            signal.addEventListener('abort', onAbort);
        }
        return controller.signal;
    };
/**
 * Takes in multiple signals and combines them into a single signal that will be
 * aborted if any of the provided signals are aborted.
 *
 * If the platform supports the native `AbortSignal.any()` method, this will be used.
 *
 * @deprecated Use `anySignal()` instad.
 * @param signals - An array of signals to observe.
 * @returns A new signal that would be aborted if any of the provided signals are aborted.
 */
export const raceSingals = anySignal;
/**
 * Creates a new AbortSignal that will be aborted after a specified timeout.
 *
 * @param ms - The number of milliseconds to wait before aborting.
 * @returns For platforms that natively support AbortSignal.timeout, this will
 *   return a native AbortSignal object. Otherwise, this will return a
 *   compatible AbortSignal microfill.
 */
export const createAbortSignalWithTimeout = typeof window !== 'undefined' &&
    typeof ((_b = window.AbortSignal) === null || _b === void 0 ? void 0 : _b.timeout) === 'function'
    ? window.AbortSignal.timeout
    : AbortSignalMicrofill.timeout;
export function createErrorFromSignal(url, signal) {
    var _a;
    return ((_a = signal.reason) === null || _a === void 0 ? void 0 : _a.name) === 'TimeoutError'
        ? new RequestTimeoutError(url, signal.reason)
        : new RequestAbortedError(url, signal.reason);
}
//# sourceMappingURL=abortcontroller.js.map